import React, { memo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { areEqual } from 'Utils/equalityChecks';
import { Modal } from 'Components/Modal';
import { PurpleButton } from 'Components/Button';
import { RoomPicker } from 'Components/SerializedEquipment';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';

import {
  listCompanyProjectsForPicker,
  getProjectDetail,
  listPropertyLocations,
  listLocationRooms,
  deployAssetToRoom,
  moveAsset,
} from '../actions';
import { EquipmentAsset, PickerOption } from '../types';

interface Props {
  isOpen: boolean;
  asset: EquipmentAsset | null;
  mode: 'deploy' | 'move';
  onClose: (e: any) => void;
  onSuccess: () => void;
  // Called on a 409 optimistic-lock conflict (move) so the parent can refresh the asset (its
  // updated_at token) before the user retries in the still-open modal.
  onConflict?: () => void;
}

// Deploy (check-in) or Move a serialized unit into a room, chosen via a Project → Location → Room
// cascade. Move requires the asset's updated_at (optimistic lock). See WEBAPP-FR-007.
function DeployAssetModalContainer({ isOpen, asset, mode, onClose, onSuccess, onConflict }: Props) {
  const dispatch = useDispatch();
  const companyId = useSelector(firstCompanyIdSelector, areEqual);

  const [projects, setProjects] = useState<PickerOption[]>([]);
  const [locations, setLocations] = useState<PickerOption[]>([]);
  const [rooms, setRooms] = useState<PickerOption[]>([]);
  const [projectId, setProjectId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [projectsFailed, setProjectsFailed] = useState(false);
  const [locationsFailed, setLocationsFailed] = useState(false);
  const [roomsFailed, setRoomsFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Optional back-dating: deploy sends it as date_in, move as moved_at. Empty = server "now".
  const [placementDate, setPlacementDate] = useState('');
  // Deploy-only optional end date (date_out): set to log a completed past stay; unit stays available.
  const [placementEndDate, setPlacementEndDate] = useState('');
  // Mint the idempotency key once per open and reuse it for every deploy/move attempt, so a retry
  // after a lost response replays the same key and the backend dedupes it instead of creating a
  // second placement. A fresh open gets a fresh key.
  const [idempotencyKey, setIdempotencyKey] = useState('');

  const loadProjects = useCallback(async () => {
    if (!companyId) return;
    setProjectsFailed(false);
    const response: any = await dispatch(listCompanyProjectsForPicker(companyId));
    if (!response) {
      setProjectsFailed(true);
      return;
    }
    const data = response?.data ?? [];
    setProjects(
      data.map((project: any) => ({ id: project.id, label: project.alias || project.uid || `Project ${project.id}` }))
    );
  }, [companyId, dispatch]);

  const loadLocations = useCallback(
    async (project: string) => {
      if (!project) return;
      setLoadingLocations(true);
      setLocationsFailed(false);
      setLocations([]);
      const detail: any = await dispatch(getProjectDetail(Number(project)));
      if (!detail?.data) {
        setLocationsFailed(true);
        setLoadingLocations(false);
        return;
      }
      const properties = detail.data.properties ?? [];
      const multiProperty = properties.length > 1;
      // Gather locations from ALL properties (not just the last) so every room is selectable.
      const locResults: any[] = await Promise.all(
        properties.map((property: any) => dispatch(listPropertyLocations(property.id)))
      );
      // Any property's location request failing = a failed destination load, not a valid partial
      // list (otherwise rooms would silently go missing).
      if (locResults.some((r: any) => !r)) {
        setLocationsFailed(true);
        setLoadingLocations(false);
        return;
      }
      // Qualify each location with its property when the project has more than one, so two
      // properties that both have a "Main Floor" aren't ambiguous.
      const options: PickerOption[] = [];
      properties.forEach((property: any, idx: number) => {
        const propertyLabel = property.name || `Property ${property.id}`;
        (locResults[idx]?.data ?? []).forEach((loc: any) => {
          const base = loc.name || `Location ${loc.id}`;
          options.push({ id: loc.id, label: multiProperty ? `${propertyLabel} — ${base}` : base });
        });
      });
      setLocations(options);
      setLoadingLocations(false);
    },
    [dispatch]
  );

  const loadRooms = useCallback(
    async (location: string) => {
      if (!location) return;
      setLoadingRooms(true);
      setRoomsFailed(false);
      setRooms([]);
      const resp: any = await dispatch(listLocationRooms(Number(location)));
      if (!resp) {
        setRoomsFailed(true);
        setLoadingRooms(false);
        return;
      }
      const data = resp?.data ?? [];
      setRooms(data.map((room: any) => ({ id: room.id, label: room.room_type?.name || `Room ${room.id}` })));
      setLoadingRooms(false);
    },
    [dispatch]
  );

  useEffect(() => {
    if (isOpen && companyId) {
      loadProjects();
    }
    if (isOpen) {
      setIdempotencyKey(uuidv4());
      setSubmitError(null);
      setPlacementDate('');
      setPlacementEndDate('');
    }
    if (!isOpen) {
      setProjects([]);
      setLocations([]);
      setRooms([]);
      setProjectId('');
      setLocationId('');
      setRoomId('');
      setProjectsFailed(false);
      setLocationsFailed(false);
      setRoomsFailed(false);
      setSubmitting(false);
    }
  }, [isOpen, companyId, loadProjects]);

  const onProjectChange = useCallback(
    (e: any) => {
      const { value } = e.target;
      setProjectId(value);
      setLocationId('');
      setRoomId('');
      setLocations([]);
      setRooms([]);
      setLocationsFailed(false);
      setRoomsFailed(false);
      loadLocations(value);
    },
    [loadLocations]
  );

  const onLocationChange = useCallback(
    (e: any) => {
      const { value } = e.target;
      setLocationId(value);
      setRoomId('');
      setRooms([]);
      setRoomsFailed(false);
      loadRooms(value);
    },
    [loadRooms]
  );

  const onRoomChange = useCallback((e: any) => setRoomId(e.target.value), []);

  const onSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();
      if (!asset || !roomId) return;

      setSubmitting(true);
      setSubmitError(null);
      const date = placementDate || undefined;
      const response: any =
        mode === 'deploy'
          ? await dispatch(
              deployAssetToRoom(asset.id, {
                room_id: Number(roomId),
                date_in: date,
                date_out: placementEndDate || undefined,
                idempotency_key: idempotencyKey,
              })
            )
          : await dispatch(
              moveAsset(asset.id, {
                to_room_id: Number(roomId),
                moved_at: date,
                updated_at: asset.updated_at,
                idempotency_key: idempotencyKey,
              })
            );
      setSubmitting(false);
      if (response?.data) {
        onSuccess();
        return;
      }
      // A move can 409 when the asset's updated_at is stale — ask the parent to refresh it so the
      // user can retry in this still-open modal. Show the backend's 409/422 message either way.
      if (response?.error?.status === 409 && onConflict) onConflict();
      setSubmitError(
        response?.error?.message
          || (mode === 'deploy' ? 'Couldn’t deploy this unit. Please try again.' : 'Couldn’t move this unit. Please try again.')
      );
    },
    [asset, roomId, mode, placementDate, placementEndDate, idempotencyKey, dispatch, onSuccess, onConflict]
  );

  const canSubmit = !!roomId && !submitting;

  return (
    <Modal
      id="deploy-asset-modal"
      title={mode === 'deploy' ? 'Deploy Equipment' : 'Move Equipment'}
      isOpen={isOpen}
      modalHeader
      dataBsBackdrop="static"
      dataBsKeyboard="false"
      modalCloseClick={onClose}
    >
      <form className="d-flex flex-column w-100" noValidate action="#" data-testid="deploy-asset-form" onSubmit={onSubmit}>
        <p className="text-muted">{asset?.name ?? 'Asset'}</p>
        <RoomPicker
          projects={projects}
          locations={locations}
          rooms={rooms}
          projectId={projectId}
          locationId={locationId}
          roomId={roomId}
          loadingLocations={loadingLocations}
          loadingRooms={loadingRooms}
          onProjectChange={onProjectChange}
          onLocationChange={onLocationChange}
          onRoomChange={onRoomChange}
        />
        {projectsFailed && (
          <div className="d-flex align-items-center gap-2 mt-2" role="alert" data-testid="projects-error">
            <span className="text-danger">Couldn’t load projects.</span>
            <button type="button" className="btn btn-link btn-sm p-0" onClick={loadProjects}>
              Retry
            </button>
          </div>
        )}
        {locationsFailed && (
          <div className="d-flex align-items-center gap-2 mt-2" role="alert" data-testid="locations-error">
            <span className="text-danger">Couldn’t load locations.</span>
            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => loadLocations(projectId)}>
              Retry
            </button>
          </div>
        )}
        {roomsFailed && (
          <div className="d-flex align-items-center gap-2 mt-2" role="alert" data-testid="rooms-error">
            <span className="text-danger">Couldn’t load rooms.</span>
            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => loadRooms(locationId)}>
              Retry
            </button>
          </div>
        )}
        <div className="mt-3 d-flex gap-2">
          <div className="flex-fill">
            <label className="form-label" htmlFor="placement-date">
              {mode === 'deploy' ? 'Deploy date (optional)' : 'Move date (optional)'}
            </label>
            <input
              id="placement-date"
              type="date"
              className="form-control"
              value={placementDate}
              onChange={(e) => setPlacementDate(e.target.value)}
              data-testid="placement-date"
            />
          </div>
          {mode === 'deploy' && (
            <div className="flex-fill">
              <label className="form-label" htmlFor="placement-end-date">
                End date (optional)
              </label>
              <input
                id="placement-end-date"
                type="date"
                className="form-control"
                value={placementEndDate}
                onChange={(e) => setPlacementEndDate(e.target.value)}
                data-testid="placement-end-date"
              />
            </div>
          )}
        </div>
        <small className="text-muted d-block mt-1">
          {mode === 'deploy'
            ? 'Deploy date blank = now. Set an end date to log a completed past stay (unit stays available).'
            : 'Leave blank to use now.'}
        </small>
        {submitError && (
          <p className="text-danger mt-2 mb-0" role="alert" data-testid="deploy-error">
            {submitError}
          </p>
        )}
        <div className="modal-footer d-flex gap-2 justify-content-end mt-3">
          <PurpleButton type="button" onClick={onClose}>
            Cancel
          </PurpleButton>
          <PurpleButton type="submit" disabled={!canSubmit}>
            {mode === 'deploy' ? 'Deploy' : 'Move'}
          </PurpleButton>
        </div>
      </form>
    </Modal>
  );
}

DeployAssetModalContainer.defaultProps = {
  onConflict: undefined,
};

const DeployAssetModalMemo = memo(DeployAssetModalContainer, areEqual);

export { DeployAssetModalMemo as DeployAssetModal };
